import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/Functions';

const AdminPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Drawer State
  const [showDrawer, setShowDrawer] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Form State for Parent + Children
  const [formData, setFormData] = useState({
    name: '', 
    description: '',
    is_featured: false,
    is_active: true,
    plans: [
      { id: null, name: '', price: '', billing_cycle: '', features: [''], button_text: 'Get Started', button_link: '', is_active: true }
    ]
  });
  const [deletedPlans, setDeletedPlans] = useState([]);

  const [saving, setSaving] = useState(false);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/packages`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      const res = await response.json();
      if (!response.ok) throw res;
      setPackages(res.data || []);
    } catch (err) {
      setError('Failed to load packages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPackages(); }, []);

  const handleOpenCreate = () => {
    setFormData({
      name: '', 
      description: '',
      is_featured: false, 
      is_active: true,
      plans: [
        { id: null, name: '', price: '', billing_cycle: '', features: [''], button_text: 'Get Started', button_link: '', is_active: true }
      ]
    });
    setDeletedPlans([]);
    setIsEditing(false);
    setCurrentId(null);
    setShowDrawer(true);
  };

  const handleOpenEdit = (pkg) => {
    setFormData({
      name: pkg.name || '',
      description: pkg.description || '',
      is_featured: !!pkg.is_featured,
      is_active: !!pkg.is_active,
      plans: (pkg.children && pkg.children.length > 0) ? pkg.children.map(c => ({
        id: c.id,
        name: c.name || '',
        price: c.price || '',
        billing_cycle: c.billing_cycle || '',
        features: (c.features && c.features.length > 0) ? c.features : [''],
        button_text: c.button_text || 'Get Started',
        button_link: c.button_link || '',
        is_active: !!c.is_active
      })) : [{ id: null, name: '', price: '', billing_cycle: '', features: [''], button_text: 'Get Started', button_link: '', is_active: true }]
    });
    setDeletedPlans([]);
    setIsEditing(true);
    setCurrentId(pkg.id);
    setShowDrawer(true);
  };

  const confirmDelete = (pkg) => setItemToDelete(pkg);

  const executeDelete = async () => {
    if (!itemToDelete) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/packages/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to delete');
      setPackages(packages.filter(p => p.id !== itemToDelete.id));
      setItemToDelete(null);
      toast.success('Package deleted successfully!');
    } catch (err) {
      toast.error('Error deleting package.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' };
      
      let parentId = currentId;
      const parentPayload = {
        name: formData.name,
        description: formData.description,
        is_featured: formData.is_featured,
        is_active: formData.is_active
      };

      if (isEditing) {
        const res = await fetch(`${API_URL}/api/admin/packages/${parentId}`, {
          method: 'PUT', headers, body: JSON.stringify(parentPayload)
        });
        if (!res.ok) throw await res.json();
      } else {
        const res = await fetch(`${API_URL}/api/admin/packages`, {
          method: 'POST', headers, body: JSON.stringify(parentPayload)
        });
        const data = await res.json();
        if (!res.ok) throw data;
        parentId = data.data.id;
      }

      for (const delId of deletedPlans) {
        await fetch(`${API_URL}/api/admin/packages/${delId}`, {
          method: 'DELETE', headers
        });
      }

      for (const plan of formData.plans) {
        const planPayload = {
          ...plan,
          parent_id: parentId,
          features: plan.features.map(f => f.trim()).filter(f => f !== '')
        };

        if (plan.id) {
          await fetch(`${API_URL}/api/admin/packages/${plan.id}`, {
            method: 'PUT', headers, body: JSON.stringify(planPayload)
          });
        } else {
          await fetch(`${API_URL}/api/admin/packages`, {
            method: 'POST', headers, body: JSON.stringify(planPayload)
          });
        }
      }

      setShowDrawer(false);
      fetchPackages();
      toast.success(isEditing ? 'Package updated successfully!' : 'Package created successfully!');
    } catch (err) {
      toast.error(err.message || 'Error saving package.');
    } finally {
      setSaving(false);
    }
  };

  const handleParentChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePlanChange = (pIndex, field, value) => {
    const newPlans = [...formData.plans];
    newPlans[pIndex][field] = value;
    setFormData({ ...formData, plans: newPlans });
  };

  const handlePlanFeatureChange = (pIndex, fIndex, value) => {
    const newPlans = [...formData.plans];
    const newFeatures = [...newPlans[pIndex].features];
    newFeatures[fIndex] = value;
    newPlans[pIndex].features = newFeatures;
    setFormData({ ...formData, plans: newPlans });
  };

  const addPlanFeature = (pIndex) => {
    const newPlans = [...formData.plans];
    newPlans[pIndex].features.push('');
    setFormData({ ...formData, plans: newPlans });
  };

  const removePlanFeature = (pIndex, fIndex) => {
    const newPlans = [...formData.plans];
    newPlans[pIndex].features.splice(fIndex, 1);
    setFormData({ ...formData, plans: newPlans });
  };

  const addPlan = () => {
    setFormData({
      ...formData,
      plans: [...formData.plans, { id: null, name: '', price: '', billing_cycle: '', features: [''], button_text: 'Get Started', button_link: '/register', is_active: true }]
    });
  };

  const removePlan = (pIndex) => {
    const planToRemove = formData.plans[pIndex];
    if (planToRemove.id) {
      setDeletedPlans([...deletedPlans, planToRemove.id]);
    }
    const newPlans = [...formData.plans];
    newPlans.splice(pIndex, 1);
    setFormData({ ...formData, plans: newPlans });
  };

  const filteredPackages = packages.filter(pkg => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const matchName = pkg.name && pkg.name.toLowerCase().includes(term);
    const matchPlans = pkg.children && pkg.children.some(child => 
      (child.name && child.name.toLowerCase().includes(term)) ||
      (child.price && child.price.toString().toLowerCase().includes(term))
    );
    return matchName || matchPlans;
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .pk-wrap { font-family: 'Inter', sans-serif; }

        /* Hero Banner */
        .pk-hero {
          background: linear-gradient(135deg, #040f1f 0%, #061830 40%, #CFA047 100%);
          border-radius: 18px; padding: 32px 36px; margin-bottom: 28px;
          display: flex; align-items: center; justify-content: space-between;
          position: relative; overflow: hidden;
        }
        .pk-hero::before { content: ''; position: absolute; top: -40px; right: -40px; width: 240px; height: 240px; border-radius: 50%; background: rgba(255,255,255,0.05); }
        .pk-hero::after { content: ''; position: absolute; bottom: -60px; right: 120px; width: 180px; height: 180px; border-radius: 50%; background: rgba(255,255,255,0.04); }
        .pk-hero-left { position: relative; z-index: 1; }
        .pk-hero-eyebrow { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .pk-hero-title { font-size: 26px; font-weight: 800; color: #ffffff; margin: 0 0 8px; line-height: 1.2; }
        .pk-hero-sub { font-size: 14px; color: rgba(255,255,255,0.8); margin: 0; }
        .pk-hero-right { position: relative; z-index: 1; display: flex; align-items: center; gap: 16px; }
        .pk-hero-stat { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); border-radius: 14px; padding: 16px 22px; text-align: center; backdrop-filter: blur(6px); }
        .pk-hero-stat-val { font-size: 28px; font-weight: 800; color: #ffffff; line-height: 1; }
        .pk-hero-stat-label { font-size: 11px; color: #CFA047; font-weight: 600; margin-top: 4px; }
        .pk-create-btn { display: flex; align-items: center; gap: 8px; padding: 11px 22px; border-radius: 10px; border: none; background: #ffffff; color: #061830; font-size: 14px; font-weight: 700; cursor: pointer; font-family: 'Inter', sans-serif; box-shadow: 0 4px 14px rgba(0,0,0,0.15); transition: all 0.2s; white-space: nowrap; }
        .pk-create-btn:hover { box-shadow: 0 6px 18px rgba(207, 160, 71,0.4); transform: translateY(-1px); }

        /* Error */
        .pk-error { background: #fff1f2; border: 1px solid #fecdd3; border-radius: 10px; padding: 12px 16px; color: #e11d48; font-size: 13px; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }

        /* Table card */
        .pk-table-card { background: #ffffff; border-radius: 14px; border: 1px solid #dde3ea; box-shadow: 0 2px 12px rgba(0,0,0,0.05); overflow: hidden; }
        .pk-table-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #eef1f5; flex-wrap: wrap; gap: 12px; }
        .pk-table-toolbar-title { font-size: 14px; font-weight: 600; color: #2d3a4b; }
        .pk-count-badge { background: #f0f4f8; color: #5a6a7e; font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 20px; margin-left: 8px; }
        .pk-search-box { position: relative; width: 280px; }
        .pk-search-box i { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9babbf; font-size: 13px; }
        .pk-search-box input { width: 100%; padding: 10px 14px 10px 38px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 13px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; transition: all 0.2s; background: #f9fbfc; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
        .pk-search-box input:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.12); background: #fff; }
        table.pk-table { width: 100%; border-collapse: collapse; }
        .pk-table thead tr { background: #f7f9fc; }
        .pk-table th { padding: 13px 18px; font-size: 11.5px; font-weight: 700; color: #8496a9; text-transform: uppercase; letter-spacing: 0.6px; border-bottom: 1px solid #eef1f5; text-align: left; }
        .pk-table td { padding: 15px 18px; font-size: 13.5px; color: #3d4f62; border-bottom: 1px solid #f0f4f8; vertical-align: middle; }
        .pk-table tbody tr:last-child td { border-bottom: none; }
        .pk-table tbody tr:hover td { background: #f7f9fc; }

        .pk-badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .pk-badge.active { background: #f8fafc; color: #061830; }
        .pk-badge.inactive { background: #f8fafc; color: #94a3b8; }
        .pk-featured { background: #fef3c7; color: #d97706; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; margin-left: 8px; }

        .pk-actions { display: flex; gap: 6px; }
        .pk-act-btn { display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #dde3ea; background: #f7f9fc; color: #6b7a90; cursor: pointer; font-size: 13px; transition: all 0.18s; }
        .pk-act-btn:hover { background: #fff; border-color: #bcc7d4; color: #1a2332; }
        .pk-act-btn.edit:hover { border-color: #CFA047; color: #CFA047; background: #f8fafc; }
        .pk-act-btn.del:hover { border-color: #fca5a5; color: #ef4444; background: #fff1f2; }

        /* Slide-in Drawer */
        .pk-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); backdrop-filter: blur(4px); z-index: 900; display: flex; justify-content: flex-end; }
        @keyframes pk-drop { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .pk-drawer { width: 620px; max-width: 95vw; background: #fff; height: 100vh; display: flex; flex-direction: column; box-shadow: -8px 0 40px rgba(0,0,0,0.18); animation: pk-drop 0.28s cubic-bezier(0.22,1,0.36,1); }
        .pk-drawer-hd { padding: 22px 28px; border-bottom: 1px solid #eef1f5; display: flex; align-items: center; justify-content: space-between; background: linear-gradient(135deg, #061830, #040f1f); flex-shrink: 0; }
        .pk-drawer-hd-left { display: flex; align-items: center; gap: 14px; }
        .pk-drawer-icon { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); display: flex; align-items: center; justify-content: center; color: #CFA047; font-size: 16px; }
        .pk-drawer-title { font-size: 17px; font-weight: 700; color: #fff; margin: 0; }
        .pk-drawer-subtitle { font-size: 12px; color: rgba(255,255,255,0.8); margin-top: 2px; }
        .pk-drawer-close { background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2); color: white; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; transition: background 0.18s; }
        .pk-drawer-close:hover { background: rgba(255,255,255,0.22); }

        .pk-form-panel { flex: 1; overflow-y: auto; padding: 28px; display: flex; flex-direction: column; gap: 20px; }

        .pk-section-title { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
        .pk-section-title::after { content: ''; flex: 1; height: 1px; background: #fdf5e6; }

        .pk-float-group { position: relative; margin-bottom: 6px; }
        .pk-float-inp { width: 100%; padding: 20px 16px 8px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 14px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; background: #f9fbfc; transition: border-color 0.18s, box-shadow 0.18s; box-sizing: border-box; }
        .pk-float-inp:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.1); background: #fff; }
        .pk-float-inp:focus + .pk-float-label, .pk-float-inp:not(:placeholder-shown) + .pk-float-label { top: 7px; font-size: 10.5px; color: #CFA047; font-weight: 700; }
        .pk-float-label { position: absolute; top: 14px; left: 16px; font-size: 13px; color: #9babbf; font-weight: 500; pointer-events: none; transition: all 0.18s; }
        .pk-float-textarea { min-height: 100px; resize: vertical; line-height: 1.6; padding-top: 22px; }
        .pk-float-select { padding-top: 20px; padding-bottom: 6px; appearance: none; background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e"); background-repeat: no-repeat; background-position: right 16px center; background-size: 16px; padding-right: 40px; cursor: pointer; }
        .pk-float-select + .pk-float-label { top: 7px; font-size: 10.5px; color: #9babbf; font-weight: 700; }
        .pk-float-select:focus + .pk-float-label { color: #CFA047; }
        .pk-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .pk-hint { font-size: 11.5px; color: #9babbf; margin-top: 4px; padding-left: 4px; }

        .pk-switch-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; background: #f9fbfc; border: 1.5px solid #dde3ea; border-radius: 10px; }
        .pk-switch-label { font-size: 13px; font-weight: 600; color: #2d3a4b; }
        .pk-switch-sub { font-size: 11px; color: #9babbf; margin-top: 2px; }
        .pk-switch { position: relative; width: 40px; height: 24px; flex-shrink: 0; }
        .pk-switch input { opacity: 0; width: 0; height: 0; }
        .pk-slider { position: absolute; inset: 0; background: #dde3ea; border-radius: 50px; cursor: pointer; transition: 0.2s; }
        .pk-slider:before { content: ''; position: absolute; width: 18px; height: 18px; left: 3px; top: 3px; background: white; border-radius: 50%; transition: 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.18); }
        .pk-switch input:checked + .pk-slider { background: #CFA047; }
        .pk-switch input:checked + .pk-slider:before { transform: translateX(16px); }

        .pk-df { padding: 18px 28px; border-top: 1px solid #eef1f5; display: flex; justify-content: space-between; align-items: center; background: #f9fbfc; flex-shrink: 0; }
        .pk-df-hint { font-size: 12px; color: #9babbf; }
        .pk-df-btns { display: flex; gap: 12px; }
        .pk-btn-cancel { padding: 10px 22px; border-radius: 9px; font-weight: 600; cursor: pointer; background: white; border: 1.5px solid #dde3ea; color: #4a5568; font-family: 'Inter', sans-serif; font-size: 13px; transition: all 0.18s; }
        .pk-btn-cancel:hover { background: #f7f9fc; }
        .pk-btn-save { padding: 10px 26px; border-radius: 9px; font-weight: 600; cursor: pointer; background: linear-gradient(135deg, #CFA047 0%, #061830 100%); border: none; color: white; font-family: 'Inter', sans-serif; font-size: 13px; box-shadow: 0 2px 8px rgba(207, 160, 71,0.3); transition: all 0.18s; display: flex; align-items: center; gap: 8px; }
        .pk-btn-save:hover { box-shadow: 0 4px 14px rgba(207, 160, 71,0.4); transform: translateY(-1px); }
        .pk-btn-save:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

        .pk-del-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.6); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 999; }
        @keyframes pk-slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      <div className="pk-wrap">
        {/* Hero Banner */}
        <div className="pk-hero">
          <div className="pk-hero-left">
            <div className="pk-hero-eyebrow"><i className="fa-solid fa-tags"></i> Package Management</div>
            <h2 className="pk-hero-title">Price Packages</h2>
            <p className="pk-hero-sub">Create and manage pricing plans to offer your clients the best value.</p>
          </div>
          <div className="pk-hero-right">
            {!loading && (
              <>
                <div className="pk-hero-stat">
                  <div className="pk-hero-stat-val">{packages.length}</div>
                  <div className="pk-hero-stat-label">Total Groups</div>
                </div>
                <div className="pk-hero-stat">
                  <div className="pk-hero-stat-val">{packages.filter(p => p.is_active).length}</div>
                  <div className="pk-hero-stat-label">Active</div>
                </div>
              </>
            )}
            <button className="pk-create-btn" onClick={handleOpenCreate}>
              <i className="fa-solid fa-plus"></i>
              Add Package Group
            </button>
          </div>
        </div>

        {error && <div className="pk-error"><i className="fa-solid fa-circle-exclamation"></i>{error}</div>}

        <div className="pk-table-card">
          <div className="pk-table-toolbar">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="pk-table-toolbar-title">All Package Groups</span>
              <span className="pk-count-badge">{filteredPackages.length}</span>
            </div>
            <div className="pk-search-box">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input 
                type="text" 
                placeholder="Search packages by name, plan or price..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
              />
            </div>
          </div>
          <table className="pk-table">
            <thead>
              <tr>
                <th>Common Name</th>
                <th>Plans & Prices</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: '#9babbf' }}>Loading packages...</td></tr>
              ) : packages.length === 0 ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '60px', color: '#9babbf' }}>No packages yet. Click "Add Package Group" to create your first one.</td></tr>
              ) : filteredPackages.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '60px' }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#cbd5e1', fontSize: '24px' }}>
                      <i className="fa-solid fa-magnifying-glass"></i>
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', marginBottom: '8px' }}>No matching packages</div>
                    <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>We couldn't find any packages matching "{searchTerm}".</div>
                    <button onClick={() => setSearchTerm('')} style={{ background: '#fff', border: '1px solid #dde3ea', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, color: '#475569' }}>Clear Search</button>
                  </td>
                </tr>
              ) : (
                filteredPackages.map(pkg => (
                  <tr key={pkg.id}>
                    <td>
                      <span style={{ fontWeight: 600, color: '#1a2332' }}>{pkg.name}</span>
                      {pkg.is_featured && <span className="pk-featured"><i className="fa-solid fa-star" style={{ marginRight: '3px' }}></i>Featured</span>}
                    </td>
                    <td>
                      {pkg.children && pkg.children.length > 0 ? (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {pkg.children.map(child => (
                            <span key={child.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', color: '#475569' }}>
                              <strong style={{ color: '#1e293b' }}>{child.name}</strong>: ${child.price}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>No plans added</span>
                      )}
                    </td>
                    <td>
                      <span className={`pk-badge ${pkg.is_active ? 'active' : 'inactive'}`}>
                        {pkg.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="pk-actions" style={{ justifyContent: 'flex-end' }}>
                        <button className="pk-act-btn edit" onClick={() => handleOpenEdit(pkg)} title="Edit"><i className="fa-solid fa-pen-to-square"></i></button>
                        <button className="pk-act-btn del" onClick={() => confirmDelete(pkg)} title="Delete"><i className="fa-solid fa-trash-can"></i></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slide-in Drawer */}
      {showDrawer && (
        <div className="pk-overlay" onClick={() => setShowDrawer(false)}>
          <div className="pk-drawer" onClick={e => e.stopPropagation()}>
            <div className="pk-drawer-hd">
              <div className="pk-drawer-hd-left">
                <div className="pk-drawer-icon"><i className="fa-solid fa-tags"></i></div>
                <div>
                  <div className="pk-drawer-title">{isEditing ? 'Edit Package Group' : 'Add Package Group'}</div>
                  <div className="pk-drawer-subtitle">{isEditing ? 'Update the details and plans below' : 'Create a new group with multiple pricing plans'}</div>
                </div>
              </div>
              <button className="pk-drawer-close" onClick={() => setShowDrawer(false)}><i className="fa-solid fa-xmark"></i></button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="pk-form-panel">
                <div>
                  <div className="pk-section-title"><i className="fa-solid fa-info-circle"></i> General Information</div>
                  <div className="pk-float-group">
                    <input className="pk-float-inp" type="text" name="name" value={formData.name} onChange={handleParentChange} placeholder=" " required />
                    <label className="pk-float-label">Common Name (e.g., Tax Packages) *</label>
                  </div>
                  
                  <div className="pk-float-group" style={{ marginTop: '12px' }}>
                    <textarea className="pk-float-inp pk-float-textarea" name="description" value={formData.description} onChange={handleParentChange} placeholder=" "></textarea>
                    <label className="pk-float-label">Short Description</label>
                  </div>

                  <div className="pk-row" style={{ marginTop: '12px' }}>
                    <div className="pk-switch-row" style={{ padding: '8px 12px' }}>
                      <div>
                        <div className="pk-switch-label">Featured</div>
                      </div>
                      <label className="pk-switch">
                        <input type="checkbox" name="is_featured" checked={formData.is_featured} onChange={handleParentChange} />
                        <span className="pk-slider"></span>
                      </label>
                    </div>
                    <div className="pk-switch-row" style={{ padding: '8px 12px' }}>
                      <div>
                        <div className="pk-switch-label">Active</div>
                      </div>
                      <label className="pk-switch">
                        <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleParentChange} />
                        <span className="pk-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="pk-section-title"><i className="fa-solid fa-layer-group"></i> Pricing Plans</div>
                  {formData.plans.map((plan, pIndex) => (
                    <div key={pIndex} style={{ background: '#f8fafc', border: '1px solid #dde3ea', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <strong style={{ fontSize: '14px', color: '#1a2332' }}>Plan {pIndex + 1}</strong>
                        {formData.plans.length > 1 && (
                          <button type="button" onClick={() => removePlan(pIndex)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                            <i className="fa-solid fa-trash-can"></i> Remove
                          </button>
                        )}
                      </div>
                      
                      <div className="pk-row">
                        <div className="pk-float-group">
                          <input className="pk-float-inp" type="text" value={plan.name} onChange={e => handlePlanChange(pIndex, 'name', e.target.value)} placeholder=" " required />
                          <label className="pk-float-label">Plan Name *</label>
                        </div>
                        <div className="pk-float-group">
                          <input className="pk-float-inp" type="number" step="0.01" value={plan.price} onChange={e => handlePlanChange(pIndex, 'price', e.target.value)} placeholder=" " required />
                          <label className="pk-float-label">Price *</label>
                        </div>
                      </div>

                      <div className="pk-row" style={{ marginTop: '12px' }}>
                        <div className="pk-float-group">
                          <select className="pk-float-inp pk-float-select" value={plan.billing_cycle} onChange={e => handlePlanChange(pIndex, 'billing_cycle', e.target.value)}>
                            <option value="">Select Cycle</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="semi-annually">Semi-Annually</option>
                            <option value="annually">Annually</option>
                            <option value="one-time">One-Time / Flat</option>
                            <option value="weekly">Weekly</option>
                            <option value="daily">Daily</option>
                          </select>
                          <label className="pk-float-label">Billing Cycle</label>
                        </div>
                        <div className="pk-switch-row" style={{ padding: '8px 12px' }}>
                          <div className="pk-switch-label">Active Plan</div>
                          <label className="pk-switch">
                            <input type="checkbox" checked={plan.is_active} onChange={e => handlePlanChange(pIndex, 'is_active', e.target.checked)} />
                            <span className="pk-slider"></span>
                          </label>
                        </div>
                      </div>

                      <div style={{ marginTop: '16px' }}>
                        <div className="pk-hint" style={{ marginBottom: '8px' }}>Features</div>
                        {plan.features.map((feature, fIndex) => (
                          <div key={fIndex} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <div className="pk-float-group" style={{ flex: 1, marginBottom: 0 }}>
                              <input className="pk-float-inp" type="text" value={feature} onChange={e => handlePlanFeatureChange(pIndex, fIndex, e.target.value)} placeholder=" " />
                              <label className="pk-float-label">Feature {fIndex + 1}</label>
                            </div>
                            {plan.features.length > 1 && (
                              <button type="button" onClick={() => removePlanFeature(pIndex, fIndex)} style={{ width: '44px', borderRadius: '10px', border: '1.5px solid #fca5a5', background: '#fff1f2', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="fa-solid fa-trash-can"></i>
                              </button>
                            )}
                          </div>
                        ))}
                        <button type="button" onClick={() => addPlanFeature(pIndex)} style={{ background: '#fff', border: '1px dashed #dde3ea', padding: '8px', borderRadius: '8px', color: '#6b7a90', fontSize: '12px', fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                          <i className="fa-solid fa-plus"></i> Add Feature
                        </button>
                      </div>

                    </div>
                  ))}

                  <button type="button" onClick={addPlan} style={{ background: '#f8fafc', border: '1.5px dashed #CFA047', padding: '14px', borderRadius: '12px', color: '#CFA047', fontSize: '14px', fontWeight: 600, cursor: 'pointer', width: '100%', transition: 'all 0.18s' }}>
                    <i className="fa-solid fa-plus"></i> Add Another Plan
                  </button>
                </div>
              </div>

              <div className="pk-df">
                <span className="pk-df-hint"><i className="fa-solid fa-asterisk" style={{ fontSize: '9px', verticalAlign: 'middle' }}></i> Required fields</span>
                <div className="pk-df-btns">
                  <button type="button" className="pk-btn-cancel" onClick={() => setShowDrawer(false)}>Cancel</button>
                  <button type="submit" className="pk-btn-save" disabled={saving}>
                    {saving ? <><i className="fa-solid fa-spinner fa-spin"></i> Saving…</> : <><i className="fa-solid fa-floppy-disk"></i> {isEditing ? 'Update Group' : 'Save Group'}</>}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="pk-del-overlay" onClick={() => setItemToDelete(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', padding: '28px 24px', borderRadius: '16px', width: '320px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', animation: 'pk-slide-up 0.2s ease-out' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 16px' }}>
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Delete Group</h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: 1.5 }}>
              Are you sure you want to delete <strong>"{itemToDelete.name}"</strong>? This will also remove all its plans.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#f1f5f9', color: '#475569' }} onClick={() => setItemToDelete(null)}>Cancel</button>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white' }} onClick={executeDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPackages;
